import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, forkJoin } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import {
  TTSAzureRawTextRequest,
  TTSAzureRequest,
  TTSChunk,
  TTSChunkCollection,
  TTSJobStatus,
  TTSRawTextRequest,
  TTSRawTextTranslationResponse,
  TTSRequest,
} from '@mhe/reader/models';
import * as readerConfigQuery from '../state/configuration/configuration.selectors';
import { getAllWidgetInstanceIDs } from '../../global-store/widgets/widgets.selectors';
import { ReaderTokenService } from '../../tokens/reader-token.service';
import { ReaderStore } from '@mhe/reader/components/reader/state';

@Injectable({ providedIn: 'root' })
export class TtsApiClient {
  private readonly ttsApiBaseUrl$: Observable<string | undefined>;
  private readonly epubIdentifier$: Observable<string | undefined>;

  private doNotCache = false;

  constructor(
    private readonly store: Store,
    private readonly readerStore: ReaderStore,
    private readonly http: HttpClient,
    private readonly readerTokenService: ReaderTokenService,
  ) {
    // get the TTS API domain from configuration
    this.ttsApiBaseUrl$ = this.store.pipe(
      select(readerConfigQuery.getTtsApi),
      filter((s: string | undefined) => typeof s === 'string' && s.length > 0),
      first(),
    );

    // Get the epub identifier from the current book's package baseUrl
    // Extract the portion after /publish/ with the first two path segments
    // Example: https://epub-cdn-dev.mheducation.com/publish/sn_a9f1f5/1/1080mp4/OPS/
    // Example: https://habitat-export-non-prod.edmesh.com/files/sn_d0ac52/
    // Example: https://hx.nonprod.mheducation.com/files/beer_sn_3/OPS/
    // In general customer-facing use this should extract: "sn_a9f1f5/1"
    // As a fallback, use mhe:source-id and vcsVersion from metadata
    this.epubIdentifier$ = this.readerStore.book$.pipe(
      first(),
      map((book) => {
        const baseUrl = book?.package?.baseUrl;
        if (baseUrl) {
          // Habitat Export content mutates, ePub Factory CDN content is immutable once published

          // check if the URL is from Habitat Export/HX
          const hxMatch = baseUrl.match(/\/files\/([^/]+)\/?/);
          if (hxMatch) {
            // apply a random string to avoid cache collisions on Habitat Export content
            this.doNotCache = true;
            return hxMatch[1] + '/' + this.randomString();
          }

          // check if the URL is from ePub Factory CDN
          const publishMatch = baseUrl.match(/\/publish\/([^/]+\/[^/]+)/);
          if (publishMatch) {
            this.doNotCache = false;
            return publishMatch[1];
          }
        }

        // Fallback: try to use mhe:source-id and vcsVersion from metadata
        const sourceId = book?.metadata?.mheMetadata?.['mhe:source-id'];
        const vcsVersion = book?.metadata?.vcsVersion;
        if (sourceId && vcsVersion) {
          this.doNotCache = true;
          return `${sourceId}/${vcsVersion}`;
        }

        // Final fallback: use a generic identifier to allow TTS to work, but without caching
        this.doNotCache = true;
        return `unknown-book/${this.randomString()}`;
      }),
    );
  }

  private randomString(len = 8): string {
    return Math.random().toString(36).substring(2, 2 + len);
  }

  /**
     * Count the number of data-tts-utterance attributes in the HTML content.
     * This tells us how many audio chunks to expect from the TTS API.
     */
  private countExpectedUtterances(html: string): number {
    const regex = /data-tts-utterance="\d+"/g;
    const matches = html.match(regex);
    return matches ? matches.length : 0;
  }

  /**
     * Map new TTS API chunk response to TTSChunk format with synthesized timing data.
     * The new API returns one MP3 per utterance, so we create a single timing entry
     * spanning the entire duration.
     */
  private mapToTTSChunk(
    chunk: { url: string, chunk: number, duration_seconds: number },
    destination: string,
    language: string,
    gender: string,
    totalChunks: number,
  ): TTSChunk {
    const durationMs = (chunk.duration_seconds || 0) * 1000;
    const chunkIndex = chunk.chunk - 1; // Convert to 0-based index

    return {
      chunkIndex,
      mp3: chunk.url,
      metadata: {
        chunkCount: totalChunks,
        requestCacheKey: destination,
        requestLang: language,
        requestGender: gender.toUpperCase() as any,
        requestVoice: null,
        maxUtterances: totalChunks,
        chunkUtteranceBounds: Array.from({ length: totalChunks }, (_, i) => ({
          chunkIndex: i,
          min: i,
          max: i + 1,
        })),
      },
      timing: [
        {
          utterances: [chunkIndex], // Single utterance per chunk
          min: 0,
          max: durationMs,
          dur: durationMs,
        },
      ],
    };
  }

  /**
     * Determine voice config to apply a TTS API payload
     */
  private createVoiceConfig(
    language: string,
    gender: string,
  ): { voice: string, gender: string } {
    // set voice based on language and gender
    const normalizedGender = gender.toLowerCase() === 'male' ? 'Male' : 'Female';

    // later feature work will have platform, langauge, and gender mapping to different voices
    // this first release uses a multilingual neural voice for a variety of supported langauges
    const voiceName = normalizedGender === 'Female'
      ? `${language}-CoraMultilingualNeural`
      : `${language}-CoraMultilingualNeural`;

    return { voice: voiceName, gender: normalizedGender };
  }

  /**
     * TTS API for chunked content
     *
     * TTS API
     *   text: XHTML
     *   language: en-US
     *   voice: en-US-CoraMultilingualNeural
     *   gender: Female,
     *   destination: context_id
     */
  createTextToSpeechJob(
    params: TTSRequest | TTSAzureRequest,
    tocItemId?: string,
  ): Observable<TTSChunk | null> {
    // Map Player API format to TTS API format
    // Player API: content, language, gender
    // TTS API: text, language (en-US format), voice, gender, destination

    // TODO implement language mapping in a future feature
    // Convert language format (e.g., 'en' to 'en-US')
    // const ttsLanguage = params.language?.includes('-')
    //   ? params.language
    //   : `${params.language || 'en'}-US`;

    // If ePub has <html xmlns="http://www.w3.org/1999/xhtml" lang="es-MX">
    // TTS is okay because our initial language is a multilingual voice
    const ttsLanguage = 'en-US';

    // Set voice based on language and gender
    const { gender, voice } = this.createVoiceConfig(ttsLanguage, params.gender);

    // Count expected utterances from the content
    const expectedChunks = this.countExpectedUtterances(params.content);

    return forkJoin({
      ttsApiBaseUrl: this.ttsApiBaseUrl$,
      token: this.readerTokenService.getToken().pipe(first()),
      epubIdentifier: this.epubIdentifier$,
      allWidgetIds: this.store.pipe(select(getAllWidgetInstanceIDs), first()),
    }).pipe(
      switchMap(({ ttsApiBaseUrl, token, epubIdentifier, allWidgetIds }) => {
        // must have epubIdentifier to proceed
        if (!epubIdentifier) {
          throw new Error('epubIdentifier is required for TTS job creation.');
        }

        // must have tocItemId to proceed
        if (!tocItemId) {
          throw new Error('tocItemId is required for TTS job creation.');
        }

        // Default path for destination when we can cache
        let destination = `${voice}/${epubIdentifier}/${tocItemId}`;

        // if the page has widgets, modify destination to avoid cache collisions
        // widgets have dynamic content that can change and we do not have the ability
        // to compare what the existing MP3 might have been generated with
        const hasWidgets = Array.isArray(allWidgetIds) && allWidgetIds.length > 0;
        if (hasWidgets) {
          // do not cache because the page has widgets
          destination = `do-not-cache/has-widgets/${this.randomString()}/${destination}`;
        } else if (this.doNotCache) {
          // do no cache for another reason (e.g. source system is mutable content)
          destination = `do-not-cache/no-widgets/${this.randomString()}/${destination}`;
        }

        // Build JSON payload
        const payload = {
          text: params.content,
          language: ttsLanguage,
          voice,
          gender,
          destination,
        };

        // this URL is for the Reader API proxy path
        const url = `${ttsApiBaseUrl}/v1/tts?chunk=true`;

        return this.http.post<Array<{ url: string, chunk: number, duration_seconds: number }>>(
          url,
          payload,
          {
            withCredentials: true,
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            }),
          },
        ).pipe(
          // Map new TTS API response to TTSChunk format with synthesized timing data
          map((chunks) => {
            if (!chunks || chunks.length === 0) {
              return null;
            }
            // Store the expected count for later polling
            this.storeExpectedChunkCount(payload.destination, expectedChunks, ttsLanguage, gender);

            // Return the first chunk in the expected format
            // Use the expected chunk count from the content, not the current response length
            // The mediator will poll for remaining chunks using fetchTextToSpeechJob
            return this.mapToTTSChunk(chunks[0], payload.destination, ttsLanguage, gender, expectedChunks);
          }),
        );
      }),
    );
  }

  /**
   * Store for tracking expected chunk counts per destination.
   * This is needed because the status endpoint doesn't tell us how many chunks to expect.
   */
  private readonly expectedChunkCounts = new Map<string, { count: number, language: string, gender: string }>();

  /**
   * Store the expected chunk count for a destination so we can retrieve it later.
   */
  private storeExpectedChunkCount(destination: string, count: number, language: string, gender: string): void {
    console.log('Expecting', count, 'chunks for destination', destination);
    this.expectedChunkCounts.set(destination, { count, language, gender });
  }

  /**
   * Get the expected chunk count for a destination.
   */
  private getExpectedChunkInfo(destination: string): { count: number, language: string, gender: string } | undefined {
    return this.expectedChunkCounts.get(destination);
  }

  /**
     * TTS API for raw text
     *
     * TTS API
     *   text: plain text
     *   language: en-US
     *   voice: en-US-CoraMultilingualNeural
     *   gender: Female
     *   destination: context_id
     *
     * Example response:
     *
     * {
     *   "url": "https://learningtools-tts.s3.us-east-1.amazonaws.com/bf7c8470-797a-4218-9b73-cffd42124358.mp3?...",
     *   "duration_seconds": 5.98
     * }
     */
  translateRawTextToSpeech(
    params: TTSRawTextRequest | TTSAzureRawTextRequest,
  ): Observable<TTSRawTextTranslationResponse> {
    // Map Player API format to TTS API format
    // Player API: text, language, gender
    // TTS API: text, language (en-US format), voice, gender, destination

    // TODO implement language mapping in a future feature
    // Convert language format (e.g., 'en' to 'en-US')
    // const ttsLanguage = params.language?.includes('-')
    //   ? params.language
    //   : `${params.language || 'en'}-US`;

    // For this first release our voice supports multiple languages
    // Future feature work will map platform, language, and gender to different voices
    const ttsLanguage = 'en-US';

    // Set voice based on language and gender
    const { gender, voice } = this.createVoiceConfig(ttsLanguage, params.gender);

    // Build JSON payload
    const payload = {
      // is .html in TTSRawTextRequest and .text in TTSAzureRawTextRequest
      text: (params as any).html ?? (params as any).text,
      language: ttsLanguage,
      voice,
      gender,

      // TODO should spot TTS require destination, this does not need cached
      destination: 'does-not-need-cached/' + Math.random().toString(36).substring(2, 15),
    };

    return forkJoin({
      ttsApiBaseUrl: this.ttsApiBaseUrl$,
      token: this.readerTokenService.getToken().pipe(first()),
    }).pipe(
      switchMap(({ ttsApiBaseUrl, token }) => {
        // this URL is for the Reader API proxy path
        const url = `${ttsApiBaseUrl}/v1/tts`;

        return this.http.post<{ url: string, durationSeconds: number }>(url, payload, {
          withCredentials: true,
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        });
      }),
      // map response to Player API format here to reduce changes in the TTS component
      map(({ url, durationSeconds }) => ({
        location: url,
        duration_seconds: durationSeconds,
      })),
    );
  }

  /**
     * TTS API for fetching text-to-speech job status
     *
     * TTS API
     *   destination: context_id
     *
     * Obtains status on an existing text-to-speech job. The response contains
     * multiple ordered chunks
     */
  fetchTextToSpeechJob(
    requestCacheKey: string,
  ): Observable<TTSChunkCollection> {
    return forkJoin({
      ttsApiBaseUrl: this.ttsApiBaseUrl$,
      token: this.readerTokenService.getToken().pipe(first()),
    }).pipe(
      switchMap(({ ttsApiBaseUrl, token }) => {
        // this URL is for the Reader API proxy path
        const url = `${ttsApiBaseUrl}/v1/tts/status`;

        return this.http.get<Array<{ url: string, chunk: number, duration_seconds: number }>>(
          url,
          {
            params: {
              destination: requestCacheKey,
            },
            withCredentials: true,
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            }),
          },
        );
      }),
      // Map new TTS API response array to TTSChunkCollection with synthesized timing data
      map((chunks) => {
        // Get the expected chunk info from storage
        const expectedInfo = this.getExpectedChunkInfo(requestCacheKey);
        const expectedCount = expectedInfo?.count ?? 0;
        const language = expectedInfo?.language ?? 'en-US';
        const gender = expectedInfo?.gender ?? 'Male';

        if (!chunks || chunks.length === 0) {
          return {
            chunkCount: expectedCount,
            chunks: [],
            processedCount: 0,
            status: expectedCount === 0 ? TTSJobStatus.NOT_FOUND : TTSJobStatus.PENDING,
            meta: {} as any,
          };
        }

        const mappedChunks = chunks.map((chunk) =>
          this.mapToTTSChunk(chunk, requestCacheKey, language, gender, expectedCount),
        );

        // Determine if the job is complete based on expected vs received chunks
        const isComplete = chunks.length >= expectedCount;
        const status = isComplete ? TTSJobStatus.COMPLETE : TTSJobStatus.PENDING;

        // Clean up the stored count if complete
        if (isComplete) {
          this.expectedChunkCounts.delete(requestCacheKey);
        }

        return {
          chunkCount: expectedCount,
          chunks: mappedChunks,
          processedCount: chunks.length,
          status,
          meta: mappedChunks[0].metadata,
        };
      }),
    );
  }
}
